import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SummaryApi from '../common';
import displayINRCurrency from '../helpers/displayCurrency';
import { 
  FiPackage, 
  FiClock, 
 
  FiCheckCircle 
} from 'react-icons/fi';
import { FaRupeeSign } from "react-icons/fa";
import { FaChevronDown } from 'react-icons/fa';

// Search Icon
const SearchIcon = () => (
  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

// Loading Skeleton Row
const SkeletonRow = () => (
  <tr className="animate-pulse">
    <td className="px-6 py-5"><div className="h-4 bg-gray-200 rounded w-12"></div></td>
    <td className="px-6 py-5"><div className="h-4 bg-gray-200 rounded w-32"></div></td>
    <td className="px-6 py-5"><div className="h-4 bg-gray-200 rounded w-40"></div></td>
    <td className="px-6 py-5"><div className="h-4 bg-gray-200 rounded w-36"></div></td>
    <td className="px-6 py-5 text-center"><div className="h-4 bg-gray-200 rounded w-24 mx-auto"></div></td>
    <td className="px-6 py-5 text-center"><div className="h-4 bg-gray-200 rounded w-20 mx-auto"></div></td>
    <td className="px-6 py-5 text-center"><div className="h-5 w-5 bg-gray-200 rounded-full mx-auto"></div></td>
  </tr>
);

// Inner Order Items Loader
const OrderItemsLoader = () => (
  <div className="flex flex-col items-center justify-center py-12">
    <div className="w-10 h-10 border-4 border-pink-200 border-t-pink-600 rounded-full animate-spin"></div>
    <p className="mt-4 text-sm text-gray-500">Loading order items...</p>
  </div>
);

const StatCard = ({ icon: Icon, title, value, gradient }) => (
  <div className={`relative overflow-hidden bg-gradient-to-br ${gradient} rounded-2xl p-6 shadow-xl border border-white/10 hover:shadow-2xl hover:scale-[1.02] transition-all duration-300`}>
    <div className="absolute -right-12 -top-12 w-48 h-48 bg-white/10 rounded-full blur-3xl" />
    <div className="relative flex items-center gap-5">
      <div className="text-5xl text-white drop-shadow-lg">
        <Icon />
      </div>
      <div>
        <p className="text-white/90 text-sm font-medium tracking-wide">{title}</p>
        <p className="text-3xl font-black text-white mt-1">{value}</p>
      </div>
    </div>
  </div>
);

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [loadingItems, setLoadingItems] = useState({}); // { orderId: true/false }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeStatus, setActiveStatus] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const navigate = useNavigate();

  const [stats, setStats] = useState({
    ordersCount: 0,
    pendingOrders: 0,
    completedOrders: 0,
    totalRevenue: 0
  });

  const ITEMS_PER_PAGE = 10;
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await fetch(SummaryApi.allorders.url, {
          method: SummaryApi.allorders.method,
          headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) throw new Error('Failed to fetch orders');

        const data = await response.json();
        if (data.status === true || data.success) {
          const sorted = (data?.data || []).sort((a, b) =>
            new Date(b.order_date_time) - new Date(a.order_date_time)
          );
          setOrders(sorted);
          updateFilteredAndStats(sorted);
        }
      } catch (err) {
        setError('Failed to load orders. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const updateFilteredAndStats = (data) => {
    let result = [...data];

    if (activeStatus !== 'All') {
      result = result.filter(o => 
        o.order_status?.toLowerCase() === activeStatus.toLowerCase()
      );
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(o =>
        o.order_code?.toLowerCase().includes(q) ||
        o.user_name?.toLowerCase().includes(q)
      );
    }

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23,59,59,999);
      result = result.filter(o => {
        const orderDate = new Date(o.order_date_time);
        return orderDate >= start && orderDate <= end;
      });
    }

    const ordersCount = result.length;
    const pendingOrders = result.filter(o => 
      o.order_status?.toLowerCase().includes('pending') || 
      o.order_status?.toLowerCase().includes('order placed')
    ).length;
    const completedOrders = result.filter(o => 
      o.order_status?.toLowerCase().includes('delivered') || 
      o.order_status?.toLowerCase().includes('completed')
    ).length;
    const totalRevenue = result.reduce((sum, o) => sum + parseFloat(o.total_amount || 0), 0);

    setStats({ ordersCount, pendingOrders, completedOrders, totalRevenue });
    setFilteredOrders(result);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (!loading && orders.length > 0) {
      updateFilteredAndStats(orders);
    }
  }, [activeStatus, searchQuery, startDate, endDate, orders, loading]);

  const loadOrderItems = async (orderId) => {
    if (loadingItems[orderId]) return;

    setLoadingItems(prev => ({ ...prev, [orderId]: true }));
    await new Promise(resolve => setTimeout(resolve, 600));
    setLoadingItems(prev => ({ ...prev, [orderId]: false }));
  };

  const toggleExpand = (id) => {
    if (expandedId !== id) {
      loadOrderItems(id);
    }
    setExpandedId(expandedId === id ? null : id);
  };

  const totalPages = Math.ceil(filteredOrders.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentOrders = filteredOrders.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const statusTabs = ['All', 'Order Placed', 'Processing', 'Shipped', 'Delivered', 'Cancelled'];
const IMAGE_BASE_URL = SummaryApi?.productImage?.url
    ? SummaryApi.productImage.url.replace("/api/uploads", "")
    : "http://192.168.1.144:3001";


  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50/30 to-white pb-12">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <h1 className="text-3xl font-extrabold text-pink-900 mb-8 tracking-tight">
          Orders Management
        </h1>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 mb-10">
          <StatCard icon={FiPackage} title="Total Orders" value={stats.ordersCount} gradient="from-pink-500 to-rose-600" />
          <StatCard icon={FiClock} title="Pending Orders" value={stats.pendingOrders} gradient="from-amber-500 to-orange-600" />
          <StatCard icon={FiCheckCircle} title="Completed Orders" value={stats.completedOrders} gradient="from-green-500 to-teal-600" />
          <StatCard icon={FaRupeeSign } title="Total Revenue" value={displayINRCurrency(stats.totalRevenue)} gradient="from-emerald-500 to-teal-600" />
        </div>

        <div className="bg-white rounded-xl shadow-md border border-pink-100 p-4 mb-8">
          <div className="flex flex-col md:flex-row gap-6 items-start md:items-end flex-wrap">
            <div className="flex flex-wrap gap-2">
              {statusTabs.map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveStatus(tab)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                    activeStatus === tab
                      ? 'bg-pink-600 text-white shadow-md'
                      : 'bg-white border border-pink-200 text-pink-700 hover:bg-pink-50'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  max={endDate || today}
                  className="px-4 py-2 border border-pink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-400"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate}
                  max={today}
                  className="px-4 py-2 border border-pink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-400"
                />
              </div>
            </div>

            <div className="relative w-full sm:w-80 mt-4 md:mt-0 md:ml-auto">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SearchIcon />
              </div>
              <input
                type="text"
                placeholder="Search By Order CODE, Customer Name..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 rounded-full border border-pink-200 focus:outline-none focus:ring-2 focus:ring-pink-400 text-sm bg-white"
              />
            </div>
          </div>
        </div>

        {/* Orders Table - Clean & Properly Aligned */}
        <div className="bg-white rounded-xl shadow-lg border border-pink-100 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-pink-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-pink-800 uppercase tracking-wider w-16">S.No</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-pink-800 uppercase tracking-wider">Order Code</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-pink-800 uppercase tracking-wider">Customer</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-pink-800 uppercase tracking-wider">Date</th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-pink-800 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-pink-800 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-pink-800 uppercase tracking-wider w-32">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <>
                  {[...Array(8)].map((_, i) => <SkeletonRow key={i} />)}
                </>
              ) : error ? (
                <tr>
                  <td colSpan={7} className="py-16 text-center text-red-600 font-medium">
                    {error}
                  </td>
                </tr>
              ) : filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-16 text-center text-gray-600">
                    <div className="text-7xl mb-4 opacity-40">📦</div>
                    <p className="text-xl font-medium">No {activeStatus.toLowerCase()} orders found</p>
                  </td>
                </tr>
              ) : (
                currentOrders.map((order, index) => {
                  const isExpanded = expandedId === order.order_id;
                  const serialNo = startIndex + index + 1;
                  const isLoadingItems = loadingItems[order.order_id];

                  return (
                    <React.Fragment key={order.order_id}>
                      <tr
                        onClick={() => toggleExpand(order.order_id)}
                        className={`cursor-pointer transition-colors ${
                          isExpanded ? 'bg-pink-100 hover:bg-pink-200' : 'hover:bg-pink-50/60'
                        }`}
                      >
                        <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-600 font-medium">{serialNo}.</td>
                        <td className="px-6 py-5 whitespace-nowrap text-sm font-bold text-gray-900">#{order.order_code}</td>
                        <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-800">{order.user_name}</td>
                        <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-600">
                          {new Date(order.order_date_time).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap text-center text-sm font-bold text-gray-900">
                          {displayINRCurrency(order.total_amount)}
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap text-center">
                          <span
                            className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border ${
                              order.order_status?.toLowerCase().includes('deliver') || 
                              order.order_status?.toLowerCase().includes('completed')
                                ? 'bg-green-100 text-green-800 border-green-200'
                                : order.order_status?.toLowerCase().includes('ship')
                                ? 'bg-blue-100 text-blue-800 border-blue-200'
                                : order.order_status?.toLowerCase().includes('cancel')
                                ? 'bg-red-100 text-red-800 border-red-200'
                                : 'bg-amber-100 text-amber-800 border-amber-200'
                            }`}
                          >
                            {order.order_status || 'Unknown'}
                          </span>
                        </td>

                        {/* Actions Column - Edit + Expand */}
                        <td className="px-6 py-5 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center gap-4">
                            {/* Edit Button */}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                const encodedId = btoa(order.order_id.toString());
                                navigate(`/admin-panel/edit-orders/${encodedId}`);
                              }}
                              className="text-blue-600 hover:text-blue-800 transition-colors p-1 rounded hover:bg-blue-50"
                              title="Edit Order"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                />
                              </svg>
                            </button>

                            {/* Expand/Collapse Button */}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleExpand(order.order_id);
                              }}
                              className="text-gray-600 hover:text-gray-900 transition-colors p-1 rounded hover:bg-gray-100"
                              title={isExpanded ? "Collapse details" : "Expand details"}
                            >
                              <FaChevronDown
                                className={`w-5 h-5 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                              />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr>
                          <td colSpan={7} className="p-0 bg-pink-50/60">
                            <div className="px-6 py-6">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                                <div className="bg-white p-6 rounded-lg shadow-sm border border-pink-100">
                                  <h4 className="font-semibold text-pink-800 mb-4 pb-2 border-b border-pink-100">
                                    Customer Details
                                  </h4>
                                  <div className="space-y-3 text-gray-700">
                                    <p><span className="font-medium">Name:</span> {order.user_name}</p>
                                    <p><span className="font-medium">Mobile:</span> {order.mobile_number}</p>
                                    <p><span className="font-medium">Address:</span> {order.order_address}</p>
                                  </div>
                                </div>

                                <div className="bg-white p-6 rounded-lg shadow-sm border border-pink-100">
                                  <h4 className="font-semibold text-pink-800 mb-4 pb-2 border-b border-pink-100">
                                    Order Information
                                  </h4>
                                  <div className="space-y-3 text-gray-700">
                                    <p><span className="font-medium">Payment Mode:</span> {order.payment_mode || '—'}</p>
                                    <p><span className="font-medium">Order Placed:</span> {new Date(order.order_date_time).toLocaleString('en-IN')}</p>
                                    <p><span className="font-medium">Status:</span> {order.order_status || 'Unknown'}</p>
                                  </div>
                                </div>
                              </div>

                              <div className="bg-white p-6 rounded-lg shadow-sm border border-pink-100">
                                <h4 className="font-semibold text-pink-800 mb-4 pb-2 border-b border-pink-100">
                                  Order Items ({order.order_items?.length || 0})
                                </h4>

                                {isLoadingItems ? (
                                  <OrderItemsLoader />
                                ) : order.order_items?.length > 0 ? (
                                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {order.order_items.map((item) => (
                                      <div
                                        key={item.order_items_id}
                                        className="border border-gray-200 rounded-lg overflow-hidden bg-white hover:shadow-md transition-shadow"
                                      >
                                        <div className="h-40 bg-gray-50 flex items-center justify-center p-3">
<img
  src={`${IMAGE_BASE_URL}/${item.product_img}`}
  alt={item.product_name}
  className="max-h-full object-contain"
/>



                                        </div>
                                        <div className="p-4">
                                          <h5 className="font-medium text-gray-900 line-clamp-2 mb-3">
                                            {item.product_name}
                                          </h5>
                                          <div className="text-sm space-y-2 text-gray-700">
                                            <p><span className="font-medium">Price:</span> ₹{item.price}</p>
                                            <p><span className="font-medium">Quantity:</span> {item.quantity}</p>
                                            <p className="font-bold text-gray-800 pt-1">
                                              Subtotal: {displayINRCurrency(item.subtotal)}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-gray-500 text-center py-10">No items found in this order</p>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && !error && filteredOrders.length > ITEMS_PER_PAGE && (
          <div className="flex justify-center items-center gap-6 mt-10">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="flex items-center gap-2 px-6 py-2.5 bg-pink-50 text-pink-700 rounded-full disabled:opacity-50 hover:bg-pink-100 transition"
            >
              ← Previous
            </button>

            <span className="text-gray-700 font-medium">
              Page {currentPage} of {totalPages}
            </span>

            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="flex items-center gap-2 px-6 py-2.5 bg-pink-50 text-pink-700 rounded-full disabled:opacity-50 hover:bg-pink-100 transition"
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Orders;